const CHANNEL_NAME = 'ebs-auction-tab-session';
const LOCK_KEY = 'ebs_auction_active_tab';
const TAB_ID_KEY = 'ebs_auction_tab_id';
const HEARTBEAT_MS = 2000;
const STALE_MS = 6000;

type TabLock = { tabId: string; at: number };
type TabMessage = { type: 'TAKEOVER'; tabId: string };

function getTabId(): string {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
        tabId = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
    return tabId;
}

function readLock(): TabLock | null {
    try {
        const raw = localStorage.getItem(LOCK_KEY);
        return raw ? JSON.parse(raw) as TabLock : null;
    } catch {
        return null;
    }
}

function writeLock(tabId: string) {
    localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId, at: Date.now() } satisfies TabLock));
}

function releaseLock(tabId: string) {
    const lock = readLock();
    if (lock?.tabId === tabId)
        localStorage.removeItem(LOCK_KEY);
}

export function startSingleTabSession(onTakeover: () => void): () => void {
    const tabId = getTabId();
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
    const lock = readLock();
    const now = Date.now();
    const otherTabActive = !!lock && lock.tabId !== tabId && now - lock.at < STALE_MS;

    writeLock(tabId);
    if (otherTabActive)
        channel?.postMessage({ type: 'TAKEOVER', tabId } satisfies TabMessage);

    const handleMessage = (event: MessageEvent<TabMessage>) => {
        if (event.data?.type !== 'TAKEOVER' || event.data.tabId === tabId)
            return;
        onTakeover();
    };

    channel?.addEventListener('message', handleMessage);

    const heartbeat = window.setInterval(() => {
        const currentLock = readLock();
        if (!currentLock || currentLock.tabId === tabId || Date.now() - currentLock.at >= STALE_MS)
            writeLock(tabId);
        else if (currentLock.tabId !== tabId)
            onTakeover();
    }, HEARTBEAT_MS);

    const handleStorage = (event: StorageEvent) => {
        if (event.key !== LOCK_KEY)
            return;
        const nextLock = readLock();
        if (nextLock && nextLock.tabId !== tabId && Date.now() - nextLock.at < STALE_MS)
            onTakeover();
    };

    window.addEventListener('storage', handleStorage);
    const handleUnload = () => releaseLock(tabId);
    window.addEventListener('pagehide', handleUnload);

    return () => {
        window.clearInterval(heartbeat);
        channel?.removeEventListener('message', handleMessage);
        channel?.close();
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener('pagehide', handleUnload);
        releaseLock(tabId);
    };
}
